import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import SheetCard from "./SheetCard";
import { fetchHotelWeb } from "../helpers/hotelDefaults";
import {
  HOTEL_CONFIG_MODEL,
  HOTEL_WEB_FIELDS,
  HOTEL_WEB_LISTS,
  STYLE_ACTION_BUTTON_PRIMARY,
  STYLE_HOTEL_INPUT,
} from "../constants";

function listText(config, name) {
  const [key, field] = name.split(".");
  return (config[`${key}.`] || [])
    .map((item) => item[field])
    .filter(Boolean)
    .join(", ");
}

function toForm(config) {
  return Object.fromEntries(
    HOTEL_WEB_FIELDS.map(([name]) => [name, config[name] || ""]),
  );
}

function WebField({ item, value, onChange }) {
  const [name, label, kind] = item;
  const Tag = kind === "area" ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-neutral-500">
        <FM id={label} />
      </span>
      <Tag
        rows={kind === "area" ? 3 : undefined}
        value={value}
        onChange={(event) => onChange(name, event.target.value)}
        className={STYLE_HOTEL_INPUT}
      />
    </label>
  );
}

function HotelWebCard() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const result = await fetchHotelWeb();
      if (cancelled || !result) return;
      setConfig(result);
      setForm(toForm(result));
      setSaved(toForm(result));
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleChange(name, value) {
    setStatus("");
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function save() {
    const changes = {};
    HOTEL_WEB_FIELDS.forEach(([name]) => {
      if (form[name] !== saved[name]) changes[name] = form[name] || null;
    });
    if (!Object.keys(changes).length) return;
    setSaving(true);
    const res = await proxy.saveQuery({
      model: HOTEL_CONFIG_MODEL,
      storeRec: { id: config.id, ...changes },
      fieldsNames: ["id"],
    });
    setSaving(false);
    if (!res || res.error) {
      setStatus("error");
      return;
    }
    setSaved(form);
    setStatus("saved");
  }

  if (!form) return null;
  const dirty = HOTEL_WEB_FIELDS.some(([name]) => form[name] !== saved[name]);

  return (
    <SheetCard title="melhous.dash.hotel.web">
      <p className="mb-3 text-xs text-neutral-500">
        <FM id="melhous.dash.hotel.web_hint" />
      </p>
      <div className="space-y-3">
        {HOTEL_WEB_FIELDS.map((item) => (
          <div key={item[0]} className="space-y-3">
            <WebField item={item} value={form[item[0]]} onChange={handleChange} />
            {item[0] === "web_long_description" &&
              HOTEL_WEB_LISTS.map(([name, label]) => (
                <p key={name} className="text-sm text-neutral-600">
                  <span className="text-neutral-500">
                    <FM id={label} />:{" "}
                  </span>
                  {listText(config, name) || "-"}
                </p>
              ))}
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={saving || !dirty}
          onClick={save}
          className={`${STYLE_ACTION_BUTTON_PRIMARY} min-h-11 disabled:opacity-50`}
        >
          <FM id={saving ? "melhous.dash.list.saving" : "melhous.dash.save"} />
        </button>
        {status && (
          <span className="text-xs text-neutral-500">
            <FM id={`melhous.dash.hotel.web_${status}`} />
          </span>
        )}
      </div>
    </SheetCard>
  );
}

export default HotelWebCard;
